import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { API_BASE_URL } from '../../../config/api';

// Image compression utility
const compressImage = (file, maxSizeKB = 512) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        let width = img.width;
        let height = img.height;
        const maxDimension = 800;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        let quality = 0.8;
        const compress = () => {
          canvas.toBlob((blob) => {
            if (blob.size > maxSizeKB * 1024 && quality > 0.1) {
              quality -= 0.1;
              compress();
            } else {
              const reader2 = new FileReader();
              reader2.onloadend = () => resolve(reader2.result);
              reader2.readAsDataURL(blob);
            }
          }, 'image/jpeg', quality);
        };
        compress();
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

const ReviewPlayerManagement = forwardRef((props, ref) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    team: '',
    position: '',
    description: '',
    image: '',
    imageSize: 0
  });

  useImperativeHandle(ref, () => ({
    openAddForm: () => {
      resetForm();
      setShowModal(true);
    }
  }));

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/review-players`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      let base64String;

      if (file.size > 512 * 1024) {
        console.log('Compressing image...');
        base64String = await compressImage(file, 512);
      } else {
        const reader = new FileReader();
        base64String = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      setImagePreview(base64String);
      setFormData({
        ...formData,
        image: base64String,
        imageSize: Math.round((base64String.length * 3) / 4)
      });
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error processing image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      alert('Please provide an image URL or upload an image');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('adminToken');
      const url = editingPlayer 
        ? `${API_BASE_URL}/api/admin/review-players/${editingPlayer._id}` 
        : `${API_BASE_URL}/api/admin/review-players`;
      const method = editingPlayer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchPlayers();
        resetForm();
        setShowModal(false);
        alert(editingPlayer ? 'Player updated successfully!' : 'Player created successfully!');
      } else {
        const error = await response.json();
        alert(error.message || 'Error saving player');
      }
    } catch (error) {
      console.error('Error saving player:', error);
      alert('Error saving player');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      sport: player.sport,
      team: player.team || '',
      position: player.position || '',
      description: player.description || '',
      image: player.image,
      imageSize: player.imageSize || 0
    });
    setImagePreview(player.image);
    setUploadMethod('url'); // Default to URL on edit
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this player? All reviews will also be deleted.')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/review-players/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchPlayers();
        alert('Player deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting player:', error);
      alert('Error deleting player');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/review-players/${id}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchPlayers();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setEditingPlayer(null);
    setFormData({
      name: '',
      sport: '',
      team: '',
      position: '',
      description: '',
      image: '',
      imageSize: 0
    });
    setImagePreview('');
    setUploadMethod('url');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Review Players Management</h1>
        <p className="text-gray-600">Manage players for the review system (separate from drawing game)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-600 font-medium">Total Players</div>
          <div className="text-2xl font-bold text-blue-700">{players.length}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-sm text-green-600 font-medium">Active</div>
          <div className="text-2xl font-bold text-green-700">
            {players.filter(p => p.isActive).length}
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-sm text-purple-600 font-medium">Total Reviews</div>
          <div className="text-2xl font-bold text-purple-700">
            {players.reduce((sum, p) => sum + (p.ratingsCount || 0), 0)}
          </div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-sm text-orange-600 font-medium">Avg Rating</div>
          <div className="text-2xl font-bold text-orange-700">
            {players.length > 0 
              ? (players.reduce((sum, p) => sum + (p.rating || 0), 0) / players.length).toFixed(1)
              : '0.0'}
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowModal(true)}
        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 mb-6 flex items-center gap-2"
      >
        <span>➕</span> Add New Player
      </button>

      {/* Players List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {players.map((player) => (
          <div key={player._id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg text-gray-800">{player.name}</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  player.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {player.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="mb-3">
                <img 
                  src={player.image} 
                  alt={player.name}
                  className="w-full h-32 object-cover rounded bg-gray-50"
                />
                {player.imageSize > 0 && (
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Size: {formatFileSize(player.imageSize)}
                  </p>
                )}
              </div>

              <div className="space-y-2 text-sm mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">Sport:</span>
                  <span className="text-gray-600">{player.sport}</span>
                </div>
                {player.team && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">Team:</span>
                    <span className="text-gray-600">{player.team}</span>
                  </div>
                )}
                {player.position && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">Position:</span>
                    <span className="text-gray-600">{player.position}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">Rating:</span>
                  <span className="text-yellow-600 font-bold">{player.rating?.toFixed(1) || '0.0'} / 5</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">Reviews:</span>
                  <span className="text-blue-600 font-bold">{player.ratingsCount || 0}</span>
                </div>
              </div>

              {player.description && (
                <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                  {player.description}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(player)}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleToggleStatus(player._id)}
                  className={`flex-1 px-4 py-2 rounded text-sm ${
                    player.isActive 
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  {player.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(player._id)}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {players.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">No review players yet.</p>
          <p className="text-gray-400">Create your first player to get started!</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">
                {editingPlayer ? 'Edit Player' : 'Add New Player'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Lionel Messi"
                  />
                </div>

                {/* Sport */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sport *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.sport}
                    onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Football, Cricket, Tennis, etc."
                  />
                </div>

                {/* Team & Position */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team
                    </label>
                    <input
                      type="text"
                      value={formData.team}
                      onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Barcelona"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position
                    </label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Forward"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Brief description of the player..."
                  />
                </div>

                {/* Image Upload Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Method *
                  </label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="url"
                        checked={uploadMethod === 'url'}
                        onChange={(e) => setUploadMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span>Image URL</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="upload"
                        checked={uploadMethod === 'upload'}
                        onChange={(e) => setUploadMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span>Upload Image</span>
                    </label>
                  </div>

                  {uploadMethod === 'url' ? (
                    <input
                      type="url"
                      required={uploadMethod === 'url' && !editingPlayer}
                      value={formData.image}
                      onChange={(e) => {
                        setFormData({ ...formData, image: e.target.value, imageSize: 0 });
                        setImagePreview(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/player-image.jpg"
                    />
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        required={uploadMethod === 'upload' && !editingPlayer}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Images over 512KB will be automatically compressed
                      </p>
                    </div>
                  )}

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="max-h-40 object-contain bg-gray-50 rounded border"
                      />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {uploading ? 'Processing...' : (editingPlayer ? 'Update Player' : 'Create Player')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowModal(false);
                    }}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReviewPlayerManagement;
